/*global Autodesk*/
import { ModelsDataService } from "@/types/LCAoM/ModelsDataService";
import { TaskProgress } from "@/types/LCAoM/TaskProgress";
import { TreeRow } from "@/types/LCAoM/TreeBuilder";
import { MODEL_TRANSFER_PHASE, MODEL_TRANSFER_STATUS, type ModelTransferPhase, type ModelTransferStatus } from "@/types/modelTransfer";
import { Co2MaterialType } from "@api/apiClient";
import { useAppSettings } from "@api/appSettings";
import { useCadModelCategories } from "@api/cadModelCategories";
import { GRID_CHECKBOX_VISIBILITY_SELECTION_FIELD } from "@components/DataGrid/components/colDef/GridVisibilityColDef";
import CustomFooterRowCount from "@components/DataGrid/components/footer/CustomFooterRowCount";
import { GridToolbarCustom } from "@components/DataGrid/components/toolbar/GridToolbarCustom";
import DataGrid from "@components/DataGrid/DataGrid";
import "@components/DataGrid/models/dataGridProps";
import { GridRowVisibilitySelectionPropagation } from "@components/DataGrid/models/gridRowVisibilitySelectionModel";
import { numberComparatorAgg, sumWithNullsAgg, textComparatorAgg, truthyAgg } from "@components/DataGrid/utils/aggregation";
import useAssignNodes from "@features/analysis/hooks/useAssignNodes";
import useAppStore from "@hooks/useAppStore";
import { useSceneTreeVersion } from "@hooks/useSceneTreeVersion";
import useSelectionManager from "@hooks/useSelectionManager";
import i18n from "@i18n/i18n";
import { Tooltip, useTheme } from "@mui/material";
import Box from "@mui/material/Box";
import {
  GRID_AGGREGATION_FUNCTIONS,
  GRID_CHECKBOX_SELECTION_FIELD,
  GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD,
  GRID_TREE_DATA_GROUPING_FIELD,
  GridCellParams,
  GridFilterItem,
  GridFilterModel,
  GridGroupNode,
  GridRow,
  GridRowModel,
  GridRowProps,
  GridRowSelectionPropagation,
} from "@mui/x-data-grid-premium";
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
import { GridInitialStatePremium } from "@mui/x-data-grid-premium/models/gridStatePremium";
import { ScopeItem } from "@stores/AppStore";
import { gridRowBackgroundColor } from "@utils/muiHelpers";
import { observer } from "mobx-react";
import { forwardRef, MutableRefObject, PropsWithChildren, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import useResizeObserver from "use-resize-observer";
import useLmViewerSync from "../../hooks/useLmViewerSync";
import { createLoadingTasks, LoadingOverlay, NoRowsOverlay } from "../LoadingOverlay";
import { TaskProgressOverlay } from "../LoadingOverlay/TaskProgressOverlay";
import { getColumns, getFilteredColumns, getGroupingColumnDef } from "./MuiAssignGrid.columns";

export interface AssignGridProps {
  apiRef: MutableRefObject<GridApiPremium>;
  /** Centralized data service for model data lookups */
  modelsDataService: ModelsDataService;
  co2MaterialTypeLookup: Record<string, Co2MaterialType>;
  scenarioId?: string;
  treeDataEnabled: boolean;
  /** When true, runs tree building on the main thread instead of a web worker */
  useMainThread?: boolean;
  /** When true, enables string interning during tree building to reduce memory usage */
  enableStringInterning?: boolean;
  /** Task progress for operations like material assignment */
  taskProgress?: TaskProgress | null;
  /** Callback when row selection changes, reports whether any rows are selected */
  onSelectionChange?: (hasSelection: boolean) => void;
  /** When false, delays tree building until data is ready (prevents stale renders) */
  isDataReady?: boolean;
  /** When true, EC material metadata is still being fetched for assigned rows */
  isCo2MaterialTypesLoading?: boolean;
  /** When true, the scenario has assigned material IDs that require metadata lookup */
  hasAssignedMaterialTypes?: boolean;
  /** When true, assigned material IDs are still being discovered from cache or server */
  isDiscoveringAssignedMaterialTypes?: boolean;
}

export interface AssignGridHandle {
  rebuildTree: () => void;
}

const gridStorageKey = "assign-grid";

const defaultInitialState: GridInitialStatePremium = {
  density: "compact",
  aggregation: {
    model: {
      siblings: "sum",

      systemId: "textComp",
      subSystemId: "textComp",
      attribute1: "textComp",
      attribute2: "textComp",
      attribute3: "textComp",

      multiplierValue: "numberComp",
      notes: "textComp",
      carbonImpactValue: "numberComp",
      carbonImpactNotes: "textComp",
      cadMaterial: "textComp",
      source: "textComp",
      modelName: "textComp",
      cadModelCategory: "textComp",
      cadFamily: "textComp",
      cadName: "textComp",

      isStructuralMaterial: "truthy",
      scopeId: "numberComp",

      quantityTotal: "sum",
      lengthTotal: "sum",
      areaTotal: "sum",
      volumeTotal: "sum",
      thickness: "sum",

      rebarVolumeTotal: "sum",
      nonModVolumeTotal: "sum",

      a1A2A3: "sum",
      a4: "sum",
      a5: "sum",
      b1: "sum",
      b2: "sum",
      b3: "sum",
      b4: "sum",
      b5: "sum",
      b6: "sum",
      b7: "sum",
      c1: "sum",
      c2: "sum",
      c3: "sum",
      c4: "sum",
      d: "sum",
      biogenic: "sum",
      totalCo2: "sum",
      a: "sum",
      b: "sum",
      c: "sum",

      assigned: "truthy",

      "material.co2MaterialType.name": "textComp",
      "material.co2MaterialType.a1A2A3": "numberComp",
      "material.co2MaterialType.unit": "textComp",
    },
  },
  pinnedColumns: {
    left: [GRID_CHECKBOX_SELECTION_FIELD, GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD, GRID_TREE_DATA_GROUPING_FIELD],
  },
  rowVisibilitySelection: [],
};

const aggregationFunctions = {
  ...GRID_AGGREGATION_FUNCTIONS,
  truthy: truthyAgg,
  sum: sumWithNullsAgg,
  textComp: textComparatorAgg,
  numberComp: numberComparatorAgg,
};

const getSystemTypeValues = (row: any) => {
  const parameters = row?.data?.modelParameters || [];
  const systemTypeParams = parameters.filter((p: any) => typeof p?.name === "string" && /system\s*type/i.test(p.name));
  return systemTypeParams
    .map((p: any) => p?.value)
    .filter((value: any) => value !== undefined && value !== null && String(value).trim() !== "");
};

const SystemTypeGridRow = forwardRef<HTMLDivElement, GridRowProps>((props, ref) => {
  const systemTypeValues = getSystemTypeValues(props.row);

  if (systemTypeValues.length === 0) {
    return <GridRow {...props} ref={ref} />;
  }

  const rowName = props.row?.data?.cadName || props.row?.label || "Element";
  const tooltipTitle = (
    <div style={{ maxWidth: "400px", color: "#FFFFFF" }}>
      <div style={{ fontWeight: "bold", marginBottom: "8px", color: "#FFFFFF" }}>
        {i18n.t("model-grid.columns.system-type")} for {rowName}
      </div>
      <pre style={{ margin: 0, whiteSpace: "pre-wrap", color: "#FFFFFF", fontFamily: "inherit" }}>
        {systemTypeValues.map((value: string) => `  ${value}`).join("\n")}
      </pre>
    </div>
  );

  return (
    <Tooltip
      title={tooltipTitle}
      placement="bottom-start"
      arrow
      componentsProps={{
        tooltip: {
          sx: {
            backgroundColor: "rgba(97, 97, 97, 0.95)",
            color: "#FFFFFF",
            fontSize: "12px",
            padding: "8px 12px",
            maxWidth: "400px",
          },
        },
        arrow: {
          sx: {
            color: "rgba(97, 97, 97, 0.95)",
          },
        },
      }}
    >
      <GridRow {...props} ref={ref} />
    </Tooltip>
  );
});

SystemTypeGridRow.displayName = "SystemTypeGridRow";

const slots = {
  toolbar: GridToolbarCustom,
  footerRowCount: CustomFooterRowCount,
  headerFilterMenu: null, // TODO: make this user configurable
  loadingOverlay: LoadingOverlay,
  noRowsOverlay: NoRowsOverlay,
  row: SystemTypeGridRow,
};

const toolbarSlotProps = {
  // TODO: the multiple search options are getting confusing
  showQuickFilter: false,
  showSearchFilter: true,
  searchFilterProps: {
    collapsible: true,
  },
};

const getTreeDataPath = (row: any) => {
  return row.path;
};

// TODO: the type inspection needs to be updated to work with TreeRow
// TODO: params should be typed properly
const getRowClassName = (params: any) => {
  if (params.row.type === "category") {
    return "mui-treetable-category";
  }
  if (params.row.type === "group") {
    // Family nodes are group type nodes with keys containing "/" (categoryId/familyIndex pattern)
    if (params.row.id && params.row.id.includes("/")) {
      return "mui-treetable-family";
    }
    return "mui-treetable-group";
  }
  if (params.row.type === "element" && params.row.leaf) {
    return "mui-treetable-element";
  }
  if (params.row.type === "element" && !params.row.leaf) {
    return "mui-treetable-group";
  }
  if (params.row.type === "component") {
    return "mui-treetable-component";
  }
  return "";
};

const rowSelectionPropagation: GridRowSelectionPropagation = {
  parents: true,
  descendants: true,
};

const rowVisibilitySelectionPropagation: GridRowVisibilitySelectionPropagation = {
  parents: true,
  descendants: true,
};

const autosizeOptions = {
  columns: undefined, // autosize all columns
  includeOutliers: true,
  includeHeaders: true,
  disableColumnVirtualization: true,
  expand: true,
};

const MuiAssignGridRef = forwardRef<AssignGridHandle, PropsWithChildren<AssignGridProps>>((props, ref) => {
  const {
    apiRef,
    modelsDataService,
    co2MaterialTypeLookup,
    scenarioId,
    treeDataEnabled,
    useMainThread,
    enableStringInterning,
    taskProgress,
    onSelectionChange,
    isDataReady = true,
    isCo2MaterialTypesLoading = false,
    hasAssignedMaterialTypes = false,
    isDiscoveringAssignedMaterialTypes = false,
  } = props;
  const theme = useTheme();
  const appStore = useAppStore();
  const sceneTreeVersion = useSceneTreeVersion();
  const selectionManager = useSelectionManager();
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
  const filterModelRef = useRef<GridFilterModel>({ items: [] });
  const containerRef = useRef<any>(null);
  const appSettings = useAppSettings();
  const cadModelCategories = useCadModelCategories();
  const groupingColumnDef = useMemo(() => getGroupingColumnDef(i18n.t("assign-grid.columns.model")), [apiRef]);
  const { isFetching, isFreshFetch, isTreeBuilding, fetchComplete, co2MaterialTypeSpecKind, treeBuildProgress, rebuildTree } = useAssignNodes({
    apiRef,
    treeDataEnabled,
    useMainThread,
    enableStringInterning,
    isDataReady,
  });

  // Expose rebuildTree via ref for parent components to trigger rebuilds
  useImperativeHandle(
    ref,
    () => ({
      rebuildTree: () => {
        rebuildTree();
      },
    }),
    [rebuildTree],
  );

  useLmViewerSync({ apiRef, lmViewer: appStore.lmViewer1 });
  const prevRowsCountRef = useRef<number>(0);

  // Consider CAD category lookup readiness to avoid GUID flicker in the category column
  const categoriesReady = Array.isArray(cadModelCategories.data) && cadModelCategories.data.length > 0;

  // Bound the grid height to the available container height (prevents page growth)
  const [containerHeight, setContainerHeight] = useState<number | undefined>(undefined);
  const [gridHeight, setGridHeight] = useState<string | undefined>(undefined);
  useResizeObserver({
    ref: containerRef,
    onResize: ({ height }) => {
      if (typeof height === "number" && height !== containerHeight) {
        setContainerHeight(height);
        const padding = 8; // small internal height padding
        setGridHeight(`${Math.max(0, height - padding)}px`);
      }
    },
  });

  const gridRows = useMemo(() => {
    // Always read fresh from sceneManager when treeVersion changes
    const rows = (appStore.sceneManager?.treeRows as any[]) || [];
    prevRowsCountRef.current = rows.length;
    return rows;
  }, [sceneTreeVersion]);

  // Clear selection when tree rows change (moved from useMemo to avoid setState during render)
  useEffect(() => {
    try {
      apiRef?.current?.setRowSelectionModel([]);
      apiRef?.current?.setCellSelectionModel({});
    } catch {
      /* noop */
    }
  }, [sceneTreeVersion, apiRef]);
  const hasRows = Array.isArray(gridRows) && gridRows.length > 0;
  const noDataMessage = useMemo(() => {
    if (selectionManager.selectCount > 0) {
      return i18n.t("assign-grid.no-data-for-model");
    }
    return i18n.t("assign-grid.select-model-to-view");
  }, [selectionManager.selectCount]);

  // Keep the overlay up while node data or unresolved EC material metadata is loading.
  const gridLoading = isFetching || isTreeBuilding || isCo2MaterialTypesLoading;

  const showMaterialTypesTask =
    isCo2MaterialTypesLoading || hasAssignedMaterialTypes || isDiscoveringAssignedMaterialTypes;

  // Loading tasks - show downloading, tree building, and EC material metadata progress
  const loadingTasks = useMemo(() => {
    const dmState = appStore.downloadManager?.state as any;
    const perModel = (dmState?.perModel || {}) as Record<string, { status?: ModelTransferStatus; phase?: ModelTransferPhase; percent?: number }>;
    const modelEntries = Object.entries(perModel);
    const completed = modelEntries.filter(
      ([, s]) => s.status === MODEL_TRANSFER_STATUS.COMPLETED || s.phase === MODEL_TRANSFER_PHASE.COMPLETE || s.phase === MODEL_TRANSFER_PHASE.CACHED,
    ).length;
    const total = dmState?.total || modelEntries.length || 0;

    // Calculate overall percent for multi-model progress
    const overallPercent = total > 0 ? modelEntries.reduce((sum, [, s]) => sum + (s.percent ?? (s.status === MODEL_TRANSFER_STATUS.COMPLETED ? 100 : 0)), 0) / total : 0;

    // For single model, extract download progress from perModel
    // Also handle case where we have entries but total isn't set yet
    const effectiveTotal = total || modelEntries.length;
    const singleModelProgress =
      effectiveTotal === 1 && modelEntries.length === 1
        ? (() => {
            const [, status] = modelEntries[0];
            return {
              percent: status.percent ?? 0,
              phase: status.phase ?? "checking",
              bytesDownloaded: 0,
              totalBytes: 0,
            };
          })()
        : null;

    return createLoadingTasks({
      isFetching,
      isFreshFetch,
      isTreeBuilding,
      fetchComplete,
      // Tree is only complete when we actually have rows (not just when tree building stops)
      treeComplete: hasRows && !isTreeBuilding,
      treeBuildProgress,
      isMetadataLoading: showMaterialTypesTask && isCo2MaterialTypesLoading,
      metadataComplete: showMaterialTypesTask && !isCo2MaterialTypesLoading,
      metadataLabel: i18n.t("loading-overlay.fetchingMaterialTypes"),
      downloadProgress: singleModelProgress,
      multiModelProgress:
        total > 1
          ? {
              models: modelEntries.map(([id]) => id),
              completedModels: completed,
              totalModels: total,
              overallPercent,
              totalBytesDownloaded: 0,
              totalBytesExpected: 0,
              phase: isFetching ? "downloading" : isTreeBuilding ? "building" : "complete",
            }
          : null,
    });
  }, [appStore.downloadManager?.state, isFetching, isFreshFetch, fetchComplete, isTreeBuilding, treeBuildProgress, hasRows, showMaterialTypesTask, isCo2MaterialTypesLoading]);

  // SlotProps - updates when loading state or tasks change
  const slotProps = useMemo(
    () => ({
      toolbar: toolbarSlotProps,
      loadingOverlay: {
        tasks: loadingTasks,
        customVariant: "tasks" as const,
      },
      noRowsOverlay: {
        // Show a loading overlay (not "No data") until rows actually exist
        isLoading: !hasRows,
        tasks: loadingTasks,
        noDataMessage,
      },
    }),
    [gridLoading, loadingTasks, noDataMessage, isFetching, isTreeBuilding, isCo2MaterialTypesLoading],
  );

  const gridKey = useMemo(() => {
    return `${sceneTreeVersion}-${treeDataEnabled}`;
  }, [sceneTreeVersion, treeDataEnabled]);

  // TODO: this looks sus, should it be in a useEffect?
  filterModelRef.current = filterModel;

  // TODO: this should be shared with MuiModelGrid
  // Initialize lookups in AppStore when data changes
  useEffect(() => {
    appStore.initCadModelCategoryLookup(cadModelCategories.data);
  }, [cadModelCategories.data, appStore]);

  // TODO: this should be shared with MuiModelGrid
  useEffect(() => {
    appStore.initScopeLookup(appSettings.data?.modelScopeSetting?.modelScopeSettingsItems as ScopeItem[]);
  }, [appSettings.data?.modelScopeSetting?.modelScopeSettingsItems, appStore]);

  // TODO: this should be shared with MuiModelGrid
  useEffect(() => {
    appStore.initModelLookup(selectionManager.selectedProject?.models);
  }, [selectionManager.selectedProject?.models, appStore]);

  const columns = useMemo(
    () => getColumns(apiRef, appStore, theme, co2MaterialTypeSpecKind, modelsDataService, co2MaterialTypeLookup, scenarioId || ""),
    [apiRef, appStore, appStore.scopeLookup, theme, co2MaterialTypeSpecKind, modelsDataService, modelsDataService.version, co2MaterialTypeLookup, scenarioId],
  );

  const co2MaterialTypesResolvedKey = useMemo(
    () => Object.keys(co2MaterialTypeLookup).sort().join("|"),
    [co2MaterialTypeLookup]
  );

  const ecDisplayContextKey = useMemo(
    () => [scenarioId ?? "", sceneTreeVersion, modelsDataService.version, co2MaterialTypesResolvedKey].join(":"),
    [scenarioId, sceneTreeVersion, modelsDataService.version, co2MaterialTypesResolvedKey]
  );

  // Rows are pushed into the grid before the by-ids /query resolves. Re-apply rows once
  // EC material metadata is available so valueGetters and group aggregations recompute.
  // Also re-run when scenario/model context or tree data changes.
  useEffect(() => {
    if (isCo2MaterialTypesLoading || !scenarioId) return;

    const rows = appStore.sceneManager?.treeRows as TreeRow[] | undefined;
    if (!rows?.length || !apiRef.current?.setRows) return;

    const nextRows = treeDataEnabled ? rows : rows.filter((row) => row.leaf);
    apiRef.current.setRows(nextRows);
  }, [
    apiRef,
    appStore.sceneManager,
    ecDisplayContextKey,
    isCo2MaterialTypesLoading,
    scenarioId,
    treeDataEnabled,
  ]);
  const { filteredColumns, columnVisibilityModel: initialVisibilityModel } = useMemo(
    () => getFilteredColumns(columns, appStore, appSettings.data?.profileLevelRebarSettings as boolean, selectionManager.selectedProject as any),
    [columns, appStore, appStore.applicationSettings?.features, selectionManager.selectedProject, appSettings.data?.profileLevelRebarSettings],
  );

  const [columnVisibilityModel, setColumnVisibilityModel] = useState<Record<string, boolean>>(initialVisibilityModel);

  const initialState = useMemo(
    () => ({
      ...defaultInitialState,
      columns: {
        columnVisibilityModel,
      },
      headerFilters: true, // this needs to be sync'd with DataGridContext.headerFilters
    }),
    [columnVisibilityModel],
  );

  const sx = useMemo(() => {
    return {
      height: gridHeight,
      maxHeight: gridHeight,
      // Alter the row background color for elements and components
      "& .MuiDataGrid-row.mui-treetable-element:not(.Mui-selected):not(:hover)": {
        backgroundColor: gridRowBackgroundColor(theme),
      },
      "& .MuiDataGrid-row.mui-treetable-component:not(.Mui-selected):not(:hover)": {
        backgroundColor: gridRowBackgroundColor(theme),
      },
      "& .MuiDataGrid-row.mui-treetable-family:not(.Mui-selected):not(:hover)": {
        backgroundColor: gridRowBackgroundColor(theme),
      },
    };
  }, [appStore.colorMode, gridHeight, theme]);

  const getAggregatePosition = useCallback((groupNode: GridGroupNode) => {
    return groupNode.depth === -1 ? "footer" : "inline";
  }, []);

  const handleRowSelectionChange = useCallback(
    (selection: any) => {
      console.log("Selection Changed:", selection);

      // Notify parent component of selection state change
      onSelectionChange?.(selection.length > 0);

      const selectedRows = apiRef.current.getSelectedRows();
      console.log("Selected Rows: ", selectedRows);

      const cadIds: string[] = selectedRows.values().reduce((acc: any, row: any) => {
        const cadId = row?.data?.cadId;
        if (cadId) {
          acc.push(cadId);
        }
        return acc;
      }, []);
      console.log("cadIds: ", cadIds);

      const urns = selectionManager.getSelectedModelUrns(selectionManager.selectedScenarioId!);
      appStore.lmViewer1?.selectByCadIds(cadIds, urns[0]);
    },
    [apiRef, appStore, selectionManager, onSelectionChange],
  );

  const handleCellSelectionModelChange = useCallback((selection: any) => {
    console.log("Cell Selection Changed:", selection);

    const ignoreCell = Object.values(selection).find((cells: any) => {
      return Object.entries(cells).find(([field, _value]: [string, any]) => field === GRID_CHECKBOX_VISIBILITY_SELECTION_FIELD);
    });
    if (ignoreCell) {
      return;
    }

    apiRef.current.setRowVisibilityModel([]);
    Object.entries(selection).forEach(([rowId, cells]: [string, any]) => {
      Object.entries(cells).forEach(([field, _value]: [string, any]) => {
        if (field === GRID_TREE_DATA_GROUPING_FIELD || field === GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD) {
          const isRowVisibilitySelected = apiRef.current.isRowVisibilitySelected(rowId);
          apiRef.current.setRowVisibility(rowId, !isRowVisibilitySelected);
        }
      });
    });
  }, []);

  const handleRowVisibilitySelectionChange = useCallback(
    (selection: any) => {
      console.log("Visibility Selection Changed:", selection);

      const visibleRows = apiRef.current.getVisibleRows();
      console.log("Visible Rows: ", visibleRows);

      const cadIds: string[] = visibleRows.values().reduce((acc: any, row: any) => {
        const cadId = row?.data?.cadId;
        if (cadId) {
          acc.push(cadId);
        }
        return acc;
      }, []);
      console.log("cadIds: ", cadIds);

      const urns = selectionManager.getSelectedModelUrns(selectionManager.selectedScenarioId!);
      appStore.lmViewer1?.isolateByCadIds(cadIds, urns[0]);
    },
    [selectionManager, appStore.lmViewer1],
  );

  const handleFilterModelChange = useCallback(
    (newFilterModel: GridFilterModel) => {
      console.log("Filter Model Changed:", newFilterModel);

      if (!newFilterModel.items.find((item: GridFilterItem) => item.field === "cadId")) {
        if (filterModelRef.current.items.find((item: GridFilterItem) => item.field === "cadId")) {
          appStore.lmViewer1?.turnOnAllVisibility();
          // TODO: we need to turn on visibility for all rows
        }
      }

      setFilterModel(newFilterModel);
    },
    [appStore.lmViewer1],
  );

  // TODO: with rebar and non-modelled editing removed, is this needed?
  const isCellEditable = useCallback((params: GridCellParams) => {
    if (params.colDef.editable && params.row.leaf) {
      return true;
    }
    return false;
  }, []);

  // TODO: with rebar and non-modelled editing removed, is this needed?
  const processRowUpdate = useCallback(async (updatedRow: GridRowModel, _originalRow: GridRowModel) => {
    console.log("processRowUpdate: ", updatedRow);
    // TODO: Rebar and Non-Modelled edit validations
    return updatedRow;
  }, []);

  // TODO: with rebar and non-modelled editing removed, is this needed?
  const processRowUpdateError = useCallback(async (error: any) => {
    console.log("processRowUpdateError: ", error);
    // TODO: Rebar and Non-Modelled edit validation errors
  }, []);

  return (
    <Box ref={containerRef} sx={{ position: "relative", display: "flex", flexGrow: 1, flexDirection: "column", maxWidth: "100vw", width: "100%", minHeight: "100%" }}>
      <DataGrid
        key={gridKey}
        apiRef={apiRef}
        treeData={treeDataEnabled}
        rowGroupingColumnMode={treeDataEnabled ? undefined : "single"}
        autosizeColumns
        autosizeTreeDataGroupingColumns
        checkboxSelection
        disableRowSelectionOnClick
        getTreeDataPath={getTreeDataPath}
        columns={filteredColumns}
        // NOTE: these prevents column dimensions from being restored https://github.com/mui/mui-x/issues/17681#issuecomment-3012528602
        // and with the useSaveGridState hook, the column dimensions are restored from localStorage
        //columnVisibilityModel={columnVisibilityModel}
        //onColumnVisibilityModelChange={handleColumnVisibilityChange}
        rowSelectionPropagation={rowSelectionPropagation}
        initialState={initialState}
        groupingColDef={groupingColumnDef}
        slots={slots}
        slotProps={slotProps}
        aggregationFunctions={aggregationFunctions}
        getAggregationPosition={getAggregatePosition}
        getRowClassName={getRowClassName}
        onRowSelectionModelChange={handleRowSelectionChange}
        onRowVisibilitySelectionModelChange={handleRowVisibilitySelectionChange}
        sx={sx}
        loading={gridLoading}
        throttleRowsMs={0}
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={processRowUpdateError}
        rowVisibilitySelection
        rowVisibilitySelectionPropagation={rowVisibilitySelectionPropagation}
        cellSelection
        onCellSelectionModelChange={handleCellSelectionModelChange}
        filterModel={filterModelRef.current}
        onFilterModelChange={handleFilterModelChange}
        rowExpander
        isCellEditable={isCellEditable}
        storageKey={gridStorageKey}
        autosizeOptions={autosizeOptions}
        autosizeOnMount
      />
      {/* Task progress overlay for operations like material assignment */}
      <TaskProgressOverlay progress={taskProgress ?? null} />
    </Box>
  );
});

const MuiAssignGrid = observer(MuiAssignGridRef);
export default MuiAssignGrid;
